import { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormValue } from 'src/components/growth/meeting-modal/types';

import { Divider, ModalCloseButton, ModalContent, ModalFooter, ModalHeader } from '@bonusly/design-system';

import { useOneOnOneMeeting } from '../../hooks/use-one-on-one-meeting';
import { UserDTO } from '../../lib/entities/user';
import { useRescheduleModalForm } from './hooks/use-reschedule-modal-form';
import { RescheduleModalBody } from './reschedule-modal-body';
import { RescheduleModalFooter } from './reschedule-modal-footer';

export interface RescheduleModalContentProps {
	onClose: VoidFunction;
	onOpenCancelModal: VoidFunction;
	meetingId: string;
	guest: UserDTO;
}

export const RescheduleModalContent: FC<RescheduleModalContentProps> = ({ onClose, onOpenCancelModal, meetingId, guest }) => {
	const { t } = useTranslation(['one_on_ones']);
	const { data: meeting } = useOneOnOneMeeting(meetingId, false);

	const form = useForm<FormValue>({
		defaultValues: {
			dayOfWeek: meeting?.event?.scheduleDayOfWeek,
			frequency: meeting?.event?.frequency,
			occurrenceInMonth: meeting?.event?.scheduleOccurrenceInMonth,
			startTime: meeting?.startTime,
			endTime: meeting?.endTime,
			meetingDate: meeting?.startTime,
		},
	});

	const { submitReschedule, isSubmitting, availableRangeDays } = useRescheduleModalForm({ onClose, meetingId, guest });

	if (!meeting) return null;

	const submit = (data: FormValue) => {
		// Validate required fields
		if (!data.startTime) {
			form.setError('startTime', {
				type: 'custom',
				message: t('meeting.modal.form.start_time_error'),
			});
			return;
		}

		if (!data.endTime) {
			form.setError('endTime', {
				type: 'custom',
				message: t('meeting.modal.form.end_time_error'),
			});
			return;
		}

		// Submit the reschedule data
		submitReschedule({
			meetingDate: data.meetingDate,
			startTime: data.startTime,
			endTime: data.endTime,
		});
	};

	return (
		<ModalContent as="form" onSubmit={form.handleSubmit(submit)}>
			<ModalHeader>{t('meeting.reschedule_modal.header')}</ModalHeader>
			<ModalCloseButton size="lg" borderRadius="full" />

			<Divider />

			<FormProvider {...form}>
				<RescheduleModalBody availableRangeDays={availableRangeDays} />
			</FormProvider>

			<ModalFooter p={5}>
				<RescheduleModalFooter isSubmitting={isSubmitting} onClose={onClose} onOpenCancelModal={onOpenCancelModal} />
			</ModalFooter>
		</ModalContent>
	);
};
